<template>
    <div class="bottom-sheet">
        <div class="sheet-top" v-on="dragListeners">
            <div class="sheet-grab"></div>
        </div>
        <slot name="top" />
        <div class="sheet-title" v-on="dragListeners"><slot name="title" /></div>

        <slot />

        <div v-if="$scopedSlots.actions" class="sheet-actions"><slot name="actions" /></div>
        <button type="button" class="sheet-dismiss" @click="dismiss">{{ dismissLabel }}</button>
    </div>
</template>

<script>
import { SHEET_DISMISS_DISTANCE, SHEET_DISMISS_SPEED } from '@/constants/ui';

// a shorter move than this reads as a twitch, not a swipe
const MIN_FLICK_DISTANCE = 20;

export default {
    name: 'BottomSheet',
    props: {
        dismissLabel: { type: String, default: 'Cancel' },
    },
    data() {
        return {
            drag: null,
        };
    },
    computed: {
        // the handle and the title take the drag; content between them, like a chart, is part of the sheet
        dragListeners() {
            return {
                touchstart: this.onTouchStart,
                touchmove: this.onTouchMove,
                touchend: this.onTouchEnd,
                touchcancel: this.onTouchEnd,
            };
        },
    },
    methods: {
        dismiss() {
            this.$FModal.hide();
        },
        shouldDismiss(distance, durationMs) {
            if (distance >= SHEET_DISMISS_DISTANCE) return true;
            if (distance < MIN_FLICK_DISTANCE) return false;

            return distance / durationMs >= SHEET_DISMISS_SPEED;
        },
        // the modal library draws the sheet box around this component, so the drag moves that box
        onTouchStart(event) {
            const wrapper = this.$el.closest('.fpm--modal-content');
            if (!wrapper) return;

            wrapper.style.transition = 'none';
            this.drag = { wrapper, startY: event.touches[0].clientY, startTime: Date.now(), distance: 0 };
        },
        onTouchMove(event) {
            if (!this.drag) return;

            this.drag.distance = event.touches[0].clientY - this.drag.startY;
            if (event.cancelable) event.preventDefault();

            this.drag.wrapper.style.transform = `translateY(${Math.max(0, this.drag.distance)}px)`;
        },
        onTouchEnd() {
            if (!this.drag) return;

            const { wrapper, distance, startTime } = this.drag;
            this.drag = null;

            if (this.shouldDismiss(distance, Date.now() - startTime)) {
                // the library's close animation runs from the resting position
                wrapper.style.transition = '';
                wrapper.style.transform = '';
                this.dismiss();
                return;
            }

            wrapper.style.transition = 'transform 150ms ease-out';
            wrapper.style.transform = '';
        },
    },
};
</script>

<style>
.bottom-sheet {
    --sheet-group: #222;
    --sheet-divider: #2f2f2f;
    --sheet-label: #6c757d;
    --sheet-field: #9aa0a6;
    --sheet-muted: #8a8f96;
    --sheet-date: #c9cdd1;
    --sheet-link: #17a2b8;
    --sheet-rule: #a97ce8;

    color: #fff;
    /* on iOS the page runs under the home indicator, so the bottom keeps clear of it */
    padding: 0 14px calc(16px + env(safe-area-inset-bottom));
    font-size: 14px;
    line-height: 1.35;
}
.bottom-sheet .mono {
    font-family: var(--font-family-monospace);
}
/* the padding sits here, not on the sheet, so a grab at the very top edge starts a drag too */
.bottom-sheet .sheet-top {
    padding-top: 8px;
    touch-action: none;
}
.bottom-sheet .sheet-grab {
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: #3a3a3a;
    margin: 0 auto 12px;
}
.bottom-sheet .sheet-title {
    touch-action: none;
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 0 2px;
    font-size: 22px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
}
.bottom-sheet .sheet-title-price {
    font-family: var(--font-family-monospace);
    font-size: 19px;
    font-weight: 400;
    color: #e9ecef;
}
.bottom-sheet .sheet-title-sub {
    font-size: 15px;
    font-weight: 400;
    color: var(--sheet-muted);
}
.bottom-sheet .sheet-oneliner {
    font-size: 13px;
    color: var(--sheet-link);
    margin: 3px 2px 0;
}
.bottom-sheet .sheet-section-label {
    font-size: 12px;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--sheet-label);
    margin: 16px 2px 6px;
}
.bottom-sheet .sheet-group {
    background: var(--sheet-group);
    border-radius: 10px;
    overflow: hidden;
}
.bottom-sheet .sheet-muted-row {
    padding: 12px;
    color: var(--sheet-muted);
    text-align: center;
}
.bottom-sheet .sheet-input {
    width: 110px;
    background: #2c2c2c;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #fff;
    padding: 2px 10px;
    text-align: right;
    font-family: var(--font-family-monospace);
    /* below 16px, iOS zooms the page when the field gets focus */
    font-size: 16px;
}
.bottom-sheet .sheet-input[type="text"] {
    width: 170px;
    text-align: left;
    font-family: inherit;
}
.bottom-sheet .sheet-input:focus {
    outline: none;
    border-color: var(--sheet-link);
}
.bottom-sheet .sheet-input.is-invalid {
    border-color: #dc3545;
}
.bottom-sheet .sheet-actions {
    margin-top: 16px;
}
.bottom-sheet .sheet-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 46px;
    border: 0;
    border-radius: 10px;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
}
.bottom-sheet .sheet-btn:disabled {
    opacity: .5;
}
.bottom-sheet .sheet-btn-primary {
    background: #007bff;
}
.bottom-sheet .sheet-btn-success {
    background: #28a745;
}
.bottom-sheet .sheet-btn-danger {
    background: #dc3545;
}
.bottom-sheet .sheet-btn-warning {
    background: #ffc107;
    color: #111;
}
.bottom-sheet .sheet-text-btn {
    background: none;
    border: 0;
    padding: 0;
    color: var(--sheet-link);
    font-size: 13.5px;
    font-weight: 600;
}
.bottom-sheet .sheet-text-btn-danger {
    color: #e4606d;
}
.bottom-sheet .sheet-dismiss {
    display: block;
    width: 100%;
    height: 42px;
    margin-top: 4px;
    background: none;
    border: 0;
    color: var(--sheet-field);
    font-size: 15px;
}
.bottom-sheet .sheet-notice {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13.5px;
    line-height: 1.4;
}
.bottom-sheet .sheet-notice-danger {
    background: rgba(220, 53, 69, .12);
    border: 1px solid rgba(220, 53, 69, .45);
    color: #f1aeb5;
}
.bottom-sheet .sheet-notice-caution {
    background: rgba(255, 193, 7, .07);
    border: 1px solid rgba(255, 193, 7, .3);
    color: #ffda6a;
}
.bottom-sheet .sheet-notice-option {
    background: rgba(23, 162, 184, .10);
    border: 1px solid rgba(23, 162, 184, .35);
}
</style>
